import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Card variant */
  variant?: 'elevated' | 'outlined' | 'filled' | 'glass';
  /** Enable hover animation */
  hoverable?: boolean;
  /** Add padding to card */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Border radius size */
  rounded?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Card title */
  title?: string;
  /** Card subtitle */
  subtitle?: string;
  /** Action elements for the header */
  action?: ReactNode;
}

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  /** Padding size */
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  /** Align footer content */
  align?: 'left' | 'center' | 'right' | 'between';
}

const variantStyles = {
  elevated: 'bg-white dark:bg-gray-800 shadow-md hover:shadow-lg',
  outlined: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700',
  filled: 'bg-gray-50 dark:bg-gray-800/50',
  glass: 'bg-white/70 dark:bg-gray-800/70 backdrop-blur-lg border border-white/30 dark:border-white/10',
};

const paddingStyles = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

const roundedStyles = {
  sm: 'rounded-lg',
  md: 'rounded-xl',
  lg: 'rounded-2xl',
  xl: 'rounded-3xl',
  '2xl': 'rounded-[2rem]',
};

/**
 * Modern Card component with multiple variants
 */
const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'elevated',
      hoverable = false,
      padding = 'none',
      rounded = 'xl',
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const classes = [
      'transition-all duration-200',
      variantStyles[variant],
      paddingStyles[padding],
      roundedStyles[rounded],
      hoverable ? 'cursor-pointer hover:-translate-y-1' : '',
      className,
    ]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

/**
 * Card Header component
 */
const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ title, subtitle, action, className = '', children, ...props }, ref) => {
    const classes = [
      'flex items-center justify-between gap-4',
      'px-6 py-5 border-b border-gray-100 dark:border-gray-700',
      className,
    ]
      .join(' ')
      .trim();

    return (
      <div ref={ref} className={classes} {...props}>
        <div className='flex-1 min-w-0'>
          {title && (
            <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100 truncate'>
              {title}
            </h3>
          )}
          {subtitle && (
            <p className='text-sm text-gray-500 dark:text-gray-400 mt-1'>{subtitle}</p>
          )}
          {children}
        </div>
        {action && <div className='flex items-center gap-2 flex-shrink-0'>{action}</div>}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

/**
 * Card Body component
 */
const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ padding = 'md', className = '', children, ...props }, ref) => {
    const paddingMap = {
      none: '',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
    };

    const classes = [paddingMap[padding], className].join(' ').trim();

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

CardBody.displayName = 'CardBody';

/**
 * Card Footer component
 */
const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ align = 'right', className = '', children, ...props }, ref) => {
    const alignStyles = {
      left: 'justify-start',
      center: 'justify-center',
      right: 'justify-end',
      between: 'justify-between',
    };

    const classes = [
      'flex items-center gap-3',
      'px-6 py-4 border-t border-gray-100 dark:border-gray-700',
      'bg-gray-50 dark:bg-gray-800/50',
      alignStyles[align],
      className,
    ]
      .join(' ')
      .trim();

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardBody, CardFooter };
export default Card;
